import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import {IntegrationActionContext, IntegrationsMarketplaceGridInstalledWebComponentSchema} from '../types';
import {GzCustomTemplateListWebComponent} from "../../../ui/gzCustomTemplateList";
import {html} from "lit";
import {DEFAULT_ITEMS_PER_PAGE, DEFAULT_NUMBER_OF_COLUMNS} from "../types"
import {
  IntegrationsMarketplaceGridInstalledNirvanaDataProvider
} from "../dataProvider/IntegrationsMarketplaceGridInstalledNirvanaDataProvider";
import {SaveIntegrationWebComponent} from "../../saveIntegration";
import {htmlEncode} from "../../../utils/escape.utils";
import {translateFn} from "../../../utils/translator.helper";
import {GzDialog} from "../../../ui/gzDialog/component/GzDialog";
import {GzDialogWebComponent} from "../../../ui/gzDialog";
import {SaveIntegrationEvents} from "../../saveIntegration/types";
import {computeGridAttributes} from "../../common/utils";
import {DEFAULT_GAP} from "../../common/constants";
import {
  IntegrationsMarketplaceGridAvailableNirvanaDataProvider
} from "../../integrationsMarketplaceGridAvailable/dataProvider/IntegrationsMarketplaceGridAvailableNirvanaDataProvider";
import {showCancelWarningDialog} from "../../../tagsManager/helpers/commonFunctions";
import {TagPanelEvents} from "../../../tagsManager/common/events/TagPanelEvents";
import {IntegrationsMarketplaceCardEvents} from "../../gzIntegrationsMarketplaceCard/types";
export class IntegrationsMarketplaceGridInstalledView extends BaseWebComponentView {
  private resizeObserver: ResizeObserver;
  private editIntegrationDialog: GzDialogWebComponent;
  public dom: {
    $wrapper: HTMLElement;
    $cardboardInstalledGrid: GzCustomTemplateListWebComponent;
  };
  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$cardboardInstalledGrid = this.shadow.querySelector('#cardboard_installed_grid') as GzCustomTemplateListWebComponent;
  }

  buildView(data: IntegrationsMarketplaceGridInstalledWebComponentSchema) {
    if (!data) {
      this.dom.$wrapper.innerHTML = '';
      return;
    }

    this.buildCardboardInstalledGrid();
    this.gridResizeListener();
  }

  private installedCardsRenderer(itemData: any) {
    return html`
      <gz-integrations-marketplace-card @card-click="${this.handleCardClick}" @action-button-click="${this.handleActionButtonClick}" @history-button-click="${this.handleHistoryButtonClick}" .data="${itemData}" .dependencies="${this.dependencies}"></gz-integrations-marketplace-card>
    `;
  }

  private readonly handleActionButtonClick = (event: any) => {
    const action = event.detail.button.action;
    if (this.isIntegrationActionContext(action)) {
      switch (action) {
        case IntegrationActionContext.Start:
          this.dom.$wrapper.dispatchEvent(
            new CustomEvent(SaveIntegrationEvents.Start, {
              bubbles: true,
              composed: true,
              detail: {
                integrationId: event.detail.data._id,
              }
            }));
          break;
        case IntegrationActionContext.Stop:
          this.dom.$wrapper.dispatchEvent(
            new CustomEvent(SaveIntegrationEvents.Stop, {
              bubbles: true,
              composed: true,
              detail: {
                integrationId: event.detail.data._id,
              }
            }));
          break;
        case IntegrationActionContext.Delete:
          if (this.dependencies.permissions.canAddEditIntegrations) {
            this.dom.$wrapper.dispatchEvent(
              new CustomEvent(SaveIntegrationEvents.Delete, {
                bubbles: true,
                composed: true,
                detail: {
                  integrationId: event.detail.data._id,
                }
              }));
          }
          break;
        default:
          this.openSaveIntegrationWindow(action, event.detail.data._id, event.detail.data.type);
          break;
      }

    }
  };

  private readonly handleCardClick = (event: any) => {
    if (this.dependencies.permissions.canAddEditIntegrations && event.detail.data.licensed) {
      this.openSaveIntegrationWindow(IntegrationActionContext.Edit, event.detail.data._id, event.detail.data.type);
    }
  }

  private readonly handleHistoryButtonClick = (event: any) => {
    this.dom.$wrapper.dispatchEvent(
      new CustomEvent(SaveIntegrationEvents.ViewLogs, {
        bubbles: true,
        composed: true,
        detail: {
          integrationId: event.detail.data._id,
        }
      })
    );
  }

  private isIntegrationActionContext(value: string): boolean {
    return Object.values(IntegrationActionContext).includes(value as IntegrationActionContext);
  }

  public openSaveIntegrationWindow(action: IntegrationActionContext, id: string | null, type: number | null): void {
    const saveIntegrationComponent = new SaveIntegrationWebComponent();
    saveIntegrationComponent.actionContext = action;
    saveIntegrationComponent.integrationId = id;
    saveIntegrationComponent.type = type;

    let windowTitle = htmlEncode(translateFn('Create integration'));
    if(action === IntegrationActionContext.Edit) {
      windowTitle = htmlEncode(translateFn('Edit integration'));
    }
    const windowConfigs: GzDialog = {
      headline: windowTitle,
      bodyContent: saveIntegrationComponent,
      isOpen: true,
      closableOutsideBox: false,
      isWindow: true,
      onCloseHandler: showCancelWarningDialog.bind(this, this.closeEditTagWindow.bind(this)),
    };

    this.editIntegrationDialog= GzDialogWebComponent.buildDialogElement(
      windowConfigs,
      document.querySelector('.mainContainer') as HTMLElement
    );

    saveIntegrationComponent.addEventListener(SaveIntegrationEvents.Cancel, showCancelWarningDialog.bind(this, this.closeEditTagWindow.bind(this)));

    saveIntegrationComponent.addEventListener(SaveIntegrationEvents.ReloadGrid, () => {
      this.editIntegrationDialog.close();
      this.reloadGridItems();
    });

    saveIntegrationComponent.injectDependencies(this.dependencies);
    saveIntegrationComponent.start().then().catch();
  }


  private closeEditTagWindow() {
    if (this.editIntegrationDialog === null) {
      return;
    }

    this.editIntegrationDialog.dismiss();
    this.reloadGridItems();
  }


  private reloadGridItems(): void {
    this.dom.$wrapper.dispatchEvent(
      new CustomEvent(SaveIntegrationEvents.ReloadGrid, {
        bubbles: true,
        composed: true,
      })
    );
  }

  private buildCardboardInstalledGrid() {
    const grid = this.dom.$cardboardInstalledGrid;
    grid.itemRenderer = this.installedCardsRenderer.bind(this);
    const {
      columns,
      pageSize,
      pageSizeOptions
    } = computeGridAttributes(grid);
    grid.columns = columns;
    grid.pageSize = pageSize;
    grid.pageSizeOptions = pageSizeOptions;
    grid.gap = DEFAULT_GAP;

    grid.dataProvider = new IntegrationsMarketplaceGridInstalledNirvanaDataProvider(this.dependencies);

    grid.injectDependencies(this.dependencies);
    grid.start();
  }

  private gridResizeListener() {
    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(this.dom.$cardboardInstalledGrid);
  }


  public unobserveGridResize() {
    this.resizeObserver.unobserve(this.dom.$cardboardInstalledGrid);
  }

  handleResize() {
    const grid = this.dom.$cardboardInstalledGrid;

    const {
      columns: newNumberOfColumns,
      pageSize: newPageSize,
      pageSizeOptions: newPageSizeOptions
    } = computeGridAttributes(grid);
    // check if the number of columns needs to change
    if (grid.columns !== newNumberOfColumns) {
      grid.columns = newNumberOfColumns;
      grid.pageSize = newPageSize;
      grid.pageSizeOptions = newPageSizeOptions;
      // Update the grid view
      grid.controller.render();
    }
  }

}
